import { supabase } from '../config/supabase';
import { getNetlifyFunctionUrl, getLambdaUrl } from '../config/config';
import { format, startOfWeek, endOfWeek, getISOWeek } from 'date-fns';

// Vista previa de la guía de despacho: detalle y montos desde el mismo Lambda
// que ejecuta el ingreso, de modo que lo mostrado sea lo que se ingresa.
export const getGuiaDetallePreview = async ({ preparativoId }) => {
  try {
    const lambdaUrl = getLambdaUrl('defontana-guia-ingresar');

    if (!lambdaUrl) {
      throw new Error('URL de Lambda para ingreso de guía no configurada');
    }

    const response = await fetch(lambdaUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preparativoId, preview: true }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'Error obteniendo vista previa del detalle');
    }

    if (!data.preview || !data.guiaData) {
      throw new Error(data.error || 'Respuesta inválida del servidor');
    }

    const { guiaData } = data;

    return {
      detalle: guiaData.detalle,
      characterCount: guiaData.characterCount,
      validationErrors: guiaData.validationErrors || [],
      isValid: guiaData.isValid,
      fecha: guiaData.fecha,
      fechaEsFallback: guiaData.fechaEsFallback,
      cantidadCajas: guiaData.cantidadCajas,
      precioUnitarioUSD: guiaData.precioUnitarioUSD,
      totalUSD: guiaData.totalUSD,
      tipoCambio: guiaData.tipoCambio
    };
  } catch (error) {
    console.error('Error en getGuiaDetallePreview:', error);
    throw error;
  }
};

// Función helper para manejar errores de Supabase
const handleSupabaseError = (error) => {
  console.error('Supabase Error:', error);
  throw new Error(error.message || 'Error en la base de datos');
};

// Servicios para Temporadas
export const temporadaAPI = {
  getAll: async () => {
    try {
      const { data, error } = await supabase
        .from('temporada')
        .select('*')
        .order('anio', { ascending: true });

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};

// Servicios para Clientes
export const clientesAPI = {
  // Obtener todos los clientes con filtros opcionales
  getAll: async (filters = {}) => {
    try {
      let query = supabase.from('clientes').select(`
        *,
        transportes:transporte_id (
          id,
          nombre,
          tipo
        )
      `);
      
      // Aplicar filtros si existen
      if (filters.search) {
        query = query.or(`nombre.ilike.%${filters.search}%,nombre_contacto.ilike.%${filters.search}%,email_contacto.ilike.%${filters.search}%`);
      }
      
      if (filters.pais && filters.pais !== 'all') {
        query = query.eq('pais', filters.pais);
      }
      
      if (filters.ciudad && filters.ciudad !== 'all') {
        query = query.eq('ciudad', filters.ciudad);
      }
      
      const { data, error } = await query.order('nombre', { ascending: true });
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo cliente
  create: async (clienteData) => {
    try {
      const { data, error } = await supabase
        .from('clientes')
        .insert([clienteData])
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar cliente
  update: async (id, clienteData) => {
    try {
      const { data, error } = await supabase
        .from('clientes')
        .update(clienteData)
        .eq('id', id)
        .select(`
          *,
          transportes:transporte_id (
            id,
            nombre,
            tipo
          )
        `)
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar cliente
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('clientes')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },
};

// Servicios para Productos
export const productosAPI = {
  // Obtener todos los productos con filtros opcionales
  getAll: async (filters = {}) => {
    try {
      let query = supabase.from('productos').select(`
        *,
        tipo_caja:tipo_caja_id (
          id,
          tipo,
          descripcion
        )
      `);
      
      // Aplicar filtros si existen
      if (filters.search) {
        query = query.or(`nombre.ilike.%${filters.search}%,code.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }
      
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }
      
      if (filters.large_cm && filters.large_cm !== 'all') {
        query = query.eq('large_cm', filters.large_cm);
      }
      
      const { data, error } = await query.order('nombre', { ascending: true });
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo producto
  create: async (productoData) => {
    try {
      const { data, error } = await supabase
        .from('productos')
        .insert([productoData])
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar producto
  update: async (id, productoData) => {
    try {
      const { data, error } = await supabase
        .from('productos')
        .update(productoData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar producto
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('productos')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },
};

// Servicios para Inventario
export const inventarioAPI = {
  getAll: async (filters = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'list',
          filters
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para ver el inventario');
        }
        throw new Error(errorData.error || 'Error al obtener inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in getAll:', error);
      throw error;
    }
  },

  create: async (inventoryData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'create',
          inventoryData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para crear inventario');
        }
        throw new Error(errorData.error || 'Error al crear inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in create:', error);
      throw error;
    }
  },

  update: async (id, inventoryData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'update',
          id,
          inventoryData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para actualizar inventario');
        }
        throw new Error(errorData.error || 'Error al actualizar inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in update:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'delete',
          id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para eliminar inventario');
        }
        throw new Error(errorData.error || 'Error al eliminar inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in delete:', error);
      throw error;
    }
  },

  addEvent: async (eventData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'addEvent',
          eventData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para agregar inventario');
        }
        throw new Error(errorData.error || 'Error al agregar inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in addEvent:', error);
      throw error;
    }
  },

  reduceEvent: async (eventData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'reduceEvent',
          eventData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para rebajar inventario');
        }
        throw new Error(errorData.error || 'Error al rebajar inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in reduceEvent:', error);
      throw error;
    }
  },

  getEvents: async (filters = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'getEvents',
          filters
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para ver el historial de inventario');
        }
        throw new Error(errorData.error || 'Error al obtener eventos de inventario');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in getEvents:', error);
      throw error;
    }
  },

  // Carga masiva de eventos desde archivo Excel
  bulkUpload: async (file) => {
    try {
      // Convertir archivo a base64
      const reader = new FileReader();
      const fileBase64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Obtener token de sesión
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      // Llamar a la función de Netlify
      const functionUrl = getNetlifyFunctionUrl('inventario-bulk-upload');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          fileBase64,
          fileName: file.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al procesar el archivo');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error in bulk upload:', error);
      throw error;
    }
  },

  getByProductIds: async (productIds) => {
    try {
      if (!productIds || productIds.length === 0) {
        return { data: [] };
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('inventario-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'getByProductIds',
          productIds
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        if (response.status === 403) {
          throw new Error('No tiene permisos para ver el inventario');
        }
        throw new Error(errorData.error || 'Error al obtener stock por productos');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in getByProductIds:', error);
      throw error;
    }
  }
};

// Servicios para Plan de Cosecha
export const planCosechaAPI = {
  getPlan: async ({ temporadaId, week }) => {
    try {
      if (!temporadaId || !week) {
        return { data: null };
      }

      // Primero obtenemos el plan base
      const { data: planData, error: planError } = await supabase
        .from('plan_produccion')
        .select('*')
        .eq('temporada_id', temporadaId)
        .eq('semana', week)
        .limit(1)
        .maybeSingle();

      if (planError && planError.code !== 'PGRST116') handleSupabaseError(planError);

      if (!planData) {
        return { data: null };
      }

      // Obtenemos los detalles desde la vista que incluye stock y pedidos calculados
      const { data: detallesData, error: detallesError } = await supabase
        .from('vista_plan_produccion_completo')
        .select('*')
        .eq('plan_id', planData.id)
        .order('producto_nombre');

      if (detallesError) handleSupabaseError(detallesError);

      const mappedDetails = (detallesData || []).map((detalle) => ({
        id: detalle.detalle_id,
        producto_id: detalle.producto_id,
        cantidad_pedidos: detalle.cantidad_pedidos,
        cantidad_planificada: detalle.cantidad_planificada,
        cantidad_real: detalle.cantidad_real,
        stock_actual: detalle.stock_actual,
        stock_campo: detalle.stock_campo,
        stock_warehouse: detalle.stock_warehouse_usa,
        pedidos_semana: detalle.pedidos_semana,
        cantidad_pendiente: detalle.cantidad_pendiente,
        diferencia_plan_pedidos: detalle.diferencia_plan_pedidos,
        diferencia_real_planificado: detalle.diferencia_real_planificado,
        porcentaje_cumplimiento: detalle.porcentaje_cumplimiento,
        producto: {
          id: detalle.producto_id,
          nombre: detalle.producto_nombre,
          code: detalle.producto_codigo,
          category: detalle.producto_categoria,
          large_cm: detalle.producto_largo,
        },
      }));

      return {
        data: {
          id: planData.id,
          temporada_id: planData.temporada_id,
          semana: planData.semana,
          estado: planData.estado,
          descripcion: planData.descripcion,
          fecha_creacion: planData.fecha_creacion,
          fecha_modificacion: planData.fecha_modificacion,
          detalles: mappedDetails,
        }
      };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  createFromOrders: async ({ temporadaId, seasonYear, week }) => {
    try {
      if (!temporadaId || !seasonYear || !week) {
        throw new Error('Faltan parámetros para crear el plan de producción');
      }

      const existing = await planCosechaAPI.getPlan({ temporadaId, week });
      if (existing?.data) {
        return existing;
      }

      const { data: pedidosData, error: pedidosError } = await supabase
        .from('pedidos')
        .select(`
          detalles:pedidos_detalle (
            producto_id,
            cantidad_cajas
          )
        `)
        .eq('temporada', seasonYear)
        .eq('semana', week);

      if (pedidosError) handleSupabaseError(pedidosError);

      const planificadosPorProducto = new Map();
      (pedidosData || []).forEach((pedido) => {
        (pedido.detalles || []).forEach((detalle) => {
          const productoId = detalle.producto_id;
          const cantidad = Number(detalle.cantidad_cajas) || 0;
          const acumulado = planificadosPorProducto.get(productoId) || 0;
          planificadosPorProducto.set(productoId, acumulado + cantidad);
        });
      });

      console.log('📊 Productos planificados:', {
        totalProductos: planificadosPorProducto.size,
        productos: Array.from(planificadosPorProducto.entries())
      });

      // Crear el plan siempre, incluso si no hay pedidos
      const { data: plan, error: planError } = await supabase
        .from('plan_produccion')
        .insert([
          {
            temporada_id: temporadaId,
            semana: week,
            estado: 'Inicial',
          },
        ])
        .select()
        .single();

      if (planError) handleSupabaseError(planError);

      // Solo insertar detalles si hay productos planificados
      if (planificadosPorProducto.size > 0) {
        const detalles = Array.from(planificadosPorProducto.entries()).map(([
          productoId,
          cantidadPedidos,
        ]) => {
          return {
            plan_id: plan.id,
            producto_id: productoId,
            cantidad_pedidos: cantidadPedidos,
            cantidad_planificada: cantidadPedidos,
            cantidad_real: null,
          };
        });

        const { error: detalleError } = await supabase
          .from('plan_produccion_detalle')
          .insert(detalles);

        if (detalleError) handleSupabaseError(detalleError);
      }

      return planCosechaAPI.getPlan({ temporadaId, week });
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  savePlan: async ({ planId, detalles, removedDetailIds = [] }) => {
    try {
      if (!planId) {
        throw new Error('Plan no especificado');
      }

      if (Array.isArray(detalles) && detalles.length > 0) {
        const payload = detalles.map((detalle) => ({
          plan_id: planId,
          producto_id: detalle.producto_id,
          cantidad_pedidos: Number(detalle.cantidad_pedidos) || 0,
          cantidad_planificada: Number(detalle.cantidad_planificada) || 0,
          cantidad_real: detalle.cantidad_real !== null && detalle.cantidad_real !== undefined ? Number(detalle.cantidad_real) : null,
          fecha_modificacion: new Date().toISOString(),
          ...(detalle.id ? { id: detalle.id } : {}),
        }));

        const { error: upsertError } = await supabase
          .from('plan_produccion_detalle')
          .upsert(payload, { onConflict: 'plan_id, producto_id' });

        if (upsertError) handleSupabaseError(upsertError);
      }

      if (Array.isArray(removedDetailIds) && removedDetailIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('plan_produccion_detalle')
          .delete()
          .in('id', removedDetailIds);

        if (deleteError) handleSupabaseError(deleteError);
      }

      const { data, error } = await supabase
        .from('plan_produccion')
        .update({ fecha_modificacion: new Date().toISOString() })
        .eq('id', planId)
        .select('temporada_id, semana')
        .maybeSingle();

      if (error) handleSupabaseError(error);

      if (!data) {
        return { data: null };
      }

      return planCosechaAPI.getPlan({
        temporadaId: data.temporada_id,
        week: data.semana,
      });
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Carga masiva de productos desde archivo Excel
  bulkUpload: async (file, temporadaId, semana) => {
    try {
      // Convertir archivo a base64
      const reader = new FileReader();
      const fileBase64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Obtener token de sesión
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      // Llamar a la función de Netlify
      const functionUrl = getNetlifyFunctionUrl('plan-cosecha-bulk-upload');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          fileBase64,
          fileName: file.name,
          temporadaId,
          semana
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al procesar el archivo');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error in bulk upload:', error);
      throw error;
    }
  },

  deletePlan: async ({ temporadaId, semana }) => {
    try {
      if (!temporadaId || !semana) {
        throw new Error('Faltan parámetros para eliminar el plan de producción');
      }

      // Primero obtenemos el plan para verificar que existe
      const { data: planData, error: planError } = await supabase
        .from('plan_produccion')
        .select('id')
        .eq('temporada_id', temporadaId)
        .eq('semana', semana)
        .maybeSingle();

      if (planError) handleSupabaseError(planError);

      if (!planData) {
        throw new Error('No existe un plan para esta temporada/semana');
      }

      // Eliminar los detalles del plan primero (por integridad referencial)
      const { error: deleteDetallesError } = await supabase
        .from('plan_produccion_detalle')
        .delete()
        .eq('plan_id', planData.id);

      if (deleteDetallesError) handleSupabaseError(deleteDetallesError);

      // Eliminar el plan
      const { error: deletePlanError } = await supabase
        .from('plan_produccion')
        .delete()
        .eq('id', planData.id);

      if (deletePlanError) handleSupabaseError(deletePlanError);

      return { data: { success: true } };
    } catch (error) {
      handleSupabaseError(error);
    }
  },
};

const mapPedidoRow = (row, options = {}) => {
  const { selectedWeek = null } = options;
  const deliveryDate = row.fecha_despacho ? new Date(row.fecha_despacho) : null;
  
  // Calculate totals from detalles
  const detalles = row.detalles || [];
  const totalAmount = detalles.reduce((sum, detalle) => sum + Number(detalle.precio_total ?? 0), 0);
  const quantity = detalles.reduce((sum, detalle) => sum + Number(detalle.cantidad_cajas ?? 0), 0);
  
  const now = new Date();

  let status = 'Pendiente';
  if (deliveryDate) {
    if (deliveryDate > new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) {
      status = 'Programado';
    } else if (deliveryDate > now) {
      status = 'Confirmado';
    } else if (format(deliveryDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')) {
      status = 'En Producción';
    } else {
      status = 'Entregado';
    }
  }

  const priority = totalAmount > 5000 ? 'Alta' : totalAmount > 2000 ? 'Media' : 'Baja';
  const type = row.tipo_pedido || 'PUNTUAL';
  const semana = row.semana ?? null;

  // Get first product for display (for backward compatibility)
  const firstDetalle = detalles[0];
  const firstProducto = firstDetalle?.productos;

  return {
    id: row.id,
    orderNumber: `PED-${String(row.id).padStart(6, '0')}`,
    deliveryId: `DEL-${String(row.id).padStart(6, '0')}`,
    seasonYear: row.temporada ?? null,
    customerId: row.cliente_id,
    cliente_id: row.cliente_id,
    customerName: row.clientes?.nombre || 'Sin cliente',
    customerCode: row.clientes?.codigo_cliente || null,
    cliente_nombre: row.clientes?.nombre || 'Sin cliente',
    productId: firstProducto?.id,
    productName: firstProducto?.nombre || (detalles.length > 1 ? `${detalles.length} productos` : 'Sin producto'),
    productCode: firstProducto?.code || null,
    quantity,
    totalAmount,
    currency: 'USD',
    deliveryDate: row.fecha_despacho,
    fecha_despacho: row.fecha_despacho,
    deliveryWeek: selectedWeek ?? semana ?? (deliveryDate ? getISOWeek(deliveryDate) : null),
    deliveryType: row.tipo_despacho,
    tipo_despacho: row.tipo_despacho,
    tipo_pedido: row.tipo_pedido,
    tipo: row.tipo,
    temporada: row.temporada,
    semana: semana,
    metodo_envio: row.metodo_envio,
    transporte_id: row.transporte_id,
    transportes: row.transportes,
    observacion: row.observacion,
    pedido_recurrente_id: row.pedido_recurrente_id,
    createdAt: row.fecha_creacion,
    estado: row.estado || 'Creado',
    status,
    priority,
    type,
    detalles: detalles.map(detalle => ({
      id: detalle.id,
      producto_id: detalle.producto_id,
      cantidad_cajas: detalle.cantidad_cajas,
      precio_unitario: detalle.precio_unitario,
      precio_total: detalle.precio_total,
      producto: detalle.productos
    }))
  };
};

const buildWeekSummary = (orders) => {
  const summary = {
    totalOrders: orders.length,
    totalAmount: orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0),
    totalAmountUSD: 0,
    totalAmountCLP: 0,
    statusCounts: {},
    priorityCounts: {},
    exportOrders: {
      count: 0,
      totalBoxes: 0,
      totalWeight: 0,
      totalVolume: 0
    },
    nacionalOrders: {
      count: 0,
      boxesByType: {}
    }
  };

  // Debug: log tipo values
  console.log('Total orders:', orders.length);
  const tipoCount = {};
  orders.forEach(o => {
    tipoCount[o.tipo || 'null'] = (tipoCount[o.tipo || 'null'] || 0) + 1;
  });
  console.log('Orders by tipo:', tipoCount);

  orders.forEach((order) => {
    summary.statusCounts[order.status] = (summary.statusCounts[order.status] || 0) + 1;
    summary.priorityCounts[order.priority] = (summary.priorityCounts[order.priority] || 0) + 1;

    // Calculate export vs nacional metrics
    if (order.tipo === 'Exportación') {
      summary.exportOrders.count += 1;
      summary.totalAmountUSD += (order.totalAmount || 0);
      
      // Sum boxes, weight, and volume from order details
      if (order.detalles && order.detalles.length > 0) {
        order.detalles.forEach((detalle) => {
          const boxes = Number(detalle.cantidad_cajas || 0);
          summary.exportOrders.totalBoxes += boxes;
          
          if (detalle.producto) {
            const weightPerBox = Number(detalle.producto.peso_total_caja_kg || 0);
            const volumePerBox = Number(detalle.producto.volumen_m3 || 0);
            summary.exportOrders.totalWeight += boxes * weightPerBox;
            summary.exportOrders.totalVolume += boxes * volumePerBox;
          }
        });
      }
    } else if (order.tipo === 'Nacional') {
      summary.nacionalOrders.count += 1;
      summary.totalAmountCLP += (order.totalAmount || 0);
      
      // Count boxes by type for nacional orders
      if (order.detalles && order.detalles.length > 0) {
        order.detalles.forEach((detalle) => {
          const boxes = Number(detalle.cantidad_cajas || 0);
          const boxType = detalle.producto?.tipo_caja?.tipo;
          if (boxType) {
            summary.nacionalOrders.boxesByType[boxType] = 
              (summary.nacionalOrders.boxesByType[boxType] || 0) + boxes;
          }
        });
      }
    }
  });

  return summary;
};

// Servicios para Pedidos
export const pedidosAPI = {
  // Obtener pedidos con filtros opcionales (por semana por defecto)
  getAll: async (filters = {}) => {
    try {
      let query = supabase
        .from('pedidos')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente,
            email_contacto,
            pais,
            ciudad
          ),
          transportes:transporte_id (
            id,
            nombre,
            tipo
          ),
          detalles:pedidos_detalle (
            id,
            producto_id,
            cantidad_cajas,
            precio_unitario,
            precio_total,
            productos:producto_id (
              id,
              nombre,
              code,
              category,
              bunches_box,
              tipo_caja_id,
              tipo_caja(tipo),
              peso_total_caja_kg,
              volumen_m3
            )
          ),
          pedido_recurrente:pedido_recurrente_id (
            id,
            semana_inicio,
            semana_fin
          )
        `);
      
      // Si no se especifica una semana, usar la semana actual
      if (!filters.week) {
        const now = new Date();
        const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Lunes
        const weekEnd = endOfWeek(now, { weekStartsOn: 1 }); // Domingo
        
        query = query
          .gte('fecha_despacho', format(weekStart, 'yyyy-MM-dd'))
          .lte('fecha_despacho', format(weekEnd, 'yyyy-MM-dd'));
      }
      
      // Aplicar filtro de cliente
      if (filters.cliente_id && filters.cliente_id !== 'all') {
        query = query.eq('cliente_id', filters.cliente_id);
      }
      
      // Aplicar filtro de tipo
      if (filters.tipo && filters.tipo !== 'all') {
        query = query.eq('tipo', filters.tipo);
      }
      
      // Aplicar filtro de estado
      if (filters.estado && filters.estado !== 'all') {
        query = query.eq('estado', filters.estado);
      }
      
      const { data, error } = await query.order('fecha_despacho', { ascending: true });
      
      if (error) handleSupabaseError(error);

      const mapped = (data || []).map(mapPedidoRow);
      return { data: mapped, weekSummary: buildWeekSummary(mapped) };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener pedidos para una semana específica
  getByWeek: async ({ year, week }, filters = {}) => {
    try {
      let query = supabase
        .from('pedidos')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente,
            email_contacto,
            pais,
            ciudad
          ),
          transportes:transporte_id (
            id,
            nombre,
            tipo
          ),
          detalles:pedidos_detalle (
            id,
            producto_id,
            cantidad_cajas,
            precio_unitario,
            precio_total,
            productos:producto_id (
              id,
              nombre,
              code,
              category,
              bunches_box,
              tipo_caja_id,
              tipo_caja(tipo),
              peso_total_caja_kg,
              volumen_m3
            )
          ),
          pedido_recurrente:pedido_recurrente_id (
            id,
            semana_inicio,
            semana_fin
          )
        `)
        .eq('temporada', year)
        .eq('semana', week);

      if (filters.customerId && filters.customerId !== 'all') {
        query = query.eq('cliente_id', filters.customerId);
      }
      
      if (filters.tipo && filters.tipo !== 'all') {
        query = query.eq('tipo', filters.tipo);
      }
      
      if (filters.estado && filters.estado !== 'all') {
        query = query.eq('estado', filters.estado);
      }

      const { data, error } = await query.order('fecha_despacho', { ascending: true });

      if (error) handleSupabaseError(error);

      const mapped = (data || []).map((row) => mapPedidoRow(row, { selectedWeek: week }));

      return {
        data: mapped,
        weekSummary: buildWeekSummary(mapped)
      };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo pedido
  create: async (pedidoData) => {
    try {
      const { detalles, ...pedidoInfo } = pedidoData;
      
      // Insertar pedido
      const { data: pedido, error: pedidoError } = await supabase
        .from('pedidos')
        .insert([pedidoInfo])
        .select()
        .single();
      
      if (pedidoError) handleSupabaseError(pedidoError);
      
      // Insertar detalles si existen
      if (detalles && detalles.length > 0) {
        const detallesConPedidoId = detalles.map(detalle => ({
          pedido_id: pedido.id,
          producto_id: detalle.producto_id,
          cantidad_cajas: detalle.cantidad_cajas,
          precio_unitario: detalle.precio_unitario,
          precio_total: detalle.precio_total
        }));
        
        const { error: detallesError } = await supabase
          .from('pedidos_detalle')
          .insert(detallesConPedidoId);
        
        if (detallesError) handleSupabaseError(detallesError);
      }
      
      return { data: pedido };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar pedido
  update: async (id, pedidoData) => {
    try {
      const { detalles, ...pedidoInfo } = pedidoData;
      
      // Actualizar pedido
      const { error: pedidoError } = await supabase
        .from('pedidos')
        .update(pedidoInfo)
        .eq('id', id)
        .select()
        .single();
      
      if (pedidoError) handleSupabaseError(pedidoError);
      
      // Actualizar detalles si existen
      if (detalles && detalles.length > 0) {
        // Primero eliminar detalles existentes
        const { error: deleteError } = await supabase
          .from('pedidos_detalle')
          .delete()
          .eq('pedido_id', id);
        
        if (deleteError) handleSupabaseError(deleteError);
        
        // Insertar nuevos detalles
        const detallesConPedidoId = detalles.map(detalle => ({
          pedido_id: id,
          producto_id: detalle.producto_id,
          cantidad_cajas: detalle.cantidad_cajas,
          precio_unitario: detalle.precio_unitario,
          precio_total: detalle.precio_total
        }));
        
        const { error: insertError } = await supabase
          .from('pedidos_detalle')
          .insert(detallesConPedidoId);
        
        if (insertError) handleSupabaseError(insertError);
      }
      
      // Obtener el pedido completo con detalles
      const { data: pedidoCompleto, error: fetchError } = await supabase
        .from('pedidos')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente,
            email_contacto
          ),
          transportes:transporte_id (
            id,
            nombre,
            tipo
          ),
          detalles:pedidos_detalle (
            id,
            producto_id,
            cantidad_cajas,
            precio_unitario,
            precio_total,
            productos:producto_id (
              id,
              nombre,
              code,
              category,
              bunches_box,
              tipo_caja_id,
              tipo_caja(tipo)
            )
          )
        `)
        .eq('id', id)
        .single();
      
      if (fetchError) handleSupabaseError(fetchError);
      
      return { data: mapPedidoRow(pedidoCompleto) };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar pedido
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('pedidos')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  updateOrderTicket: async (orderId, ticketDespacho) => {
    try {
      const { data, error } = await supabase
        .from('pedidos')
        .update({ ticket_despacho: ticketDespacho })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  fetchNacionalOrdersByWeek: async (temporadaId, semana) => {
    try {
      console.log('[fetchNacionalOrdersByWeek] Query params:', { temporadaId, semana });
      
      // Use temporadaId directly as the year (the table stores year, not ID)
      const anio = temporadaId;
      console.log('[fetchNacionalOrdersByWeek] Using year:', anio);
      
      const { data, error } = await supabase
        .from('pedidos')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente,
            pais
          ),
          detalles:pedidos_detalle (
            id,
            producto_id,
            cantidad_cajas,
            productos:producto_id (
              id,
              nombre,
              code,
              category
            )
          ),
          pedido_factura_local (
            folio,
            fecha,
            sync_event_id,
            onedrive_pdf_url
          )
        `)
        .eq('temporada', anio)
        .eq('semana', semana)
        .order('id', { ascending: true });
      
      console.log('[fetchNacionalOrdersByWeek] Raw data from DB:', { 
        count: data?.length, 
        error,
        orders: data?.map(o => ({ id: o.id, cliente: o.clientes?.nombre, pais: o.clientes?.pais }))
      });
      
      if (error) handleSupabaseError(error);
      
      // Filter by national clients (not USA or Canada) on the frontend
      const filteredData = (data || []).filter(order => {
        const pais = order.clientes?.pais?.toUpperCase();
        const isExportacion = pais === 'USA' || pais === 'CANADA' || pais === 'CANADÁ';
        const isNacional = !isExportacion;
        console.log('[fetchNacionalOrdersByWeek] Order filter:', { 
          id: order.id, 
          cliente: order.clientes?.nombre,
          pais, 
          isExportacion, 
          isNacional 
        });
        return isNacional;
      });
      
      console.log('[fetchNacionalOrdersByWeek] Filtered data:', { 
        count: filteredData.length,
        orders: filteredData.map(o => ({ id: o.id, cliente: o.clientes?.nombre }))
      });
      
      return { data: filteredData };
    } catch (error) {
      console.error('[fetchNacionalOrdersByWeek] Error:', error);
      handleSupabaseError(error);
    }
  },

  checkInventarioStatus: async (temporadaId, semana) => {
    try {
      // Use temporadaId directly as the year
      const anio = temporadaId;
      
      const { data, error } = await supabase
        .from('despacho_nacional_inventario')
        .select('*')
        .eq('temporada', anio)
        .eq('semana', semana)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        handleSupabaseError(error);
      }
      
      return { data: data || null };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  rebajarInventarioNacional: async (temporadaId, semana) => {
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const netlifyUrl = getNetlifyFunctionUrl('despacho-nacionales-operations');
      const response = await fetch(netlifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          operation: 'rebajar_inventario',
          temporada: temporadaId,
          semana: semana
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al rebajar inventario');
      }

      return result;
    } catch (error) {
      console.error('Error in rebajarInventarioNacional:', error);
      throw error;
    }
  },
};

// Servicios para Preparativos de Exportación
export const preparativosAPI = {
  // Obtener preparativo por temporada y semana
  getBySeasonWeek: async ({ temporadaId, week }) => {
    try {
      if (!temporadaId || !week) {
        return { data: null };
      }

      const { data, error } = await supabase
        .from('preparativos_embarque')
        .select(`
          *,
          solicitud:preparativo_solicitud_embarque(*),
          propuesta:preparativo_propuesta_embarque(
            *,
            tarjas(*)
          ),
          sag:preparativo_agendamiento_sag(*),
          transporte:preparativo_transporte(*),
          guia_despacho:preparativo_guiadespacho(*),
          etiquetas:preparativo_etiquetas(*),
          etiquetas_kms:preparativo_etiquetas_kms(*),
          dus:preparativo_dus(*),
          factura_local:preparativo_facturalocal(*)
        `)
        .eq('temporada_id', temporadaId)
        .eq('semana', week)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener información completa del preparativo para vista de información
  getCompleteInfo: async ({ temporadaId, week }) => {
    try {
      if (!temporadaId || !week) {
        return { data: null };
      }

      const { data, error } = await supabase
        .from('preparativos_embarque')
        .select(`
          id,
          temporada_id,
          semana,
          estado,
          avance,
          solicitud:preparativo_solicitud_embarque(
            numero_awb,
            fecha_embarque
          ),
          transporte:preparativo_transporte(
            nombre_chofer,
            apellido_chofer,
            rut_chofer,
            patente_camion
          ),
          guia_despacho:preparativo_guiadespacho(
            numero_guia
          ),
          sag:preparativo_agendamiento_sag(
            fecha_hora_confirmada
          ),
          dus:preparativo_dus(
            numero_dus_inicial,
            folio_instructivo
          )
        `)
        .eq('temporada_id', temporadaId)
        .eq('semana', week)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo preparativo
  create: async ({ temporadaId, week }) => {
    try {
      const { data, error } = await supabase
        .from('preparativos_embarque')
        .insert([{
          temporada_id: temporadaId,
          semana: week,
          usuario_creacion: 'current_user' // TODO: Replace with actual user
        }])
        .select()
        .single();

      if (error) handleSupabaseError(error);

      // Create related records for each step
      const preparativoId = data.id;

      await Promise.all([
        supabase.from('preparativo_solicitud_embarque').insert([{ preparativo_id: preparativoId }]),
        supabase.from('preparativo_propuesta_embarque').insert([{ preparativo_id: preparativoId }]),
        supabase.from('preparativo_agendamiento_sag').insert([{ preparativo_id: preparativoId }]),
        supabase.from('preparativo_transporte').insert([{ preparativo_id: preparativoId }]),
        supabase.from('preparativo_guiadespacho').insert([{ preparativo_id: preparativoId }]),
        supabase.from('preparativo_etiquetas').insert([{ preparativo_id: preparativoId }]),
        supabase.from('preparativo_dus').insert([{ preparativo_id: preparativoId }])
      ]);

      return preparativosAPI.getBySeasonWeek({ temporadaId, week });
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar estado de un paso
  updateStepStatus: async ({ preparativoId, step, status }) => {
    try {
      // Mapeo de nombres de pasos a nombres de columnas en la BD
      const stepFieldMapping = {
        'solicitud': 'solicitud_embarque',
        'propuesta': 'propuesta_embarque',
        'sag': 'agendamiento_sag',
        'transporte': 'coordinar_transporte',
        'guia': 'guia_despacho',
        'etiquetas': 'impresion_etiquetas',
        'dus': 'solicitud_dus'
      };

      const mappedStep = stepFieldMapping[step] || step;
      const statusField = `estado_${mappedStep}`;
      const dateField = `fecha_actualizacion_${step}`;

      const { data, error } = await supabase
        .from('preparativos_embarque')
        .update({
          [statusField]: status,
          [dateField]: new Date().toISOString(),
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', preparativoId)
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar datos de solicitud de embarque (Paso 1)
  updateSolicitud: async ({ preparativoId, solicitudData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_solicitud_embarque')
        .upsert({
          preparativo_id: preparativoId,
          ...solicitudData,
          fecha_modificacion: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar datos de propuesta de embarque (Paso 2)
  updatePropuesta: async ({ preparativoId, propuestaData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_propuesta_embarque')
        .upsert({
          preparativo_id: preparativoId,
          ...propuestaData,
          fecha_modificacion: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar datos de agendamiento SAG (Paso 3)
  updateSAG: async ({ preparativoId, sagData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_agendamiento_sag')
        .upsert({
          preparativo_id: preparativoId,
          ...sagData,
          fecha_modificacion: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar datos de transporte (Paso 4)
  updateTransporte: async ({ preparativoId, transporteData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_transporte')
        .upsert({
          preparativo_id: preparativoId,
          ...transporteData,
          fecha_modificacion: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar datos de etiquetas (Paso 5)
  updateEtiquetas: async ({ preparativoId, etiquetasData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_etiquetas')
        .upsert({
          preparativo_id: preparativoId,
          ...etiquetasData,
          fecha_modificacion: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar datos de DUS (Paso 6)
  updateDUS: async ({ preparativoId, dusData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_dus')
        .upsert({
          preparativo_id: preparativoId,
          ...dusData,
          fecha_modificacion: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Generar Instructivo de Embarque
  generarInstructivoEmbarque: async ({ preparativoId }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('generar-instructivo-embarque');
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ preparativoId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al generar Instructivo de Embarque');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error en generarInstructivoEmbarque:', error);
      throw error;
    }
  },

  // Obtener URL de descarga fresca de OneDrive
  getOneDriveDownloadUrl: async ({ filePath }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('get-onedrive-download-url');
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ filePath })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al obtener URL de descarga');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error en getOneDriveDownloadUrl:', error);
      throw error;
    }
  },

  // Actualizar estado y avance general del preparativo
  updateEstadoYAvance: async ({ preparativoId, estado, avance }) => {
    try {
      const { data, error } = await supabase
        .from('preparativos_embarque')
        .update({
          estado,
          avance,
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', preparativoId)
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Generar propuesta de embarque desde pedidos de una semana
  generarPropuestaEmbarque: async ({ propuestaId, temporadaId, semana }) => {
    try {
      // Obtener token de sesión
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('generar-propuesta-embarque');
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ propuestaId, temporadaId, semana })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al generar propuesta de embarque');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error en generarPropuestaEmbarque:', error);
      throw error;
    }
  },

  // Obtener detalles de propuesta de embarque
  getPropuestaDetalles: async ({ propuestaId }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_propuesta_embarque_detalles')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente
          ),
          productos:producto_id (
            id,
            nombre,
            code,
            category,
            bunches_box,
            stems_bunches,
            large_cm,
            peso_neto_ruscus_kg,
            peso_total_caja_kg,
            volumen_m3,
            tipo_caja_id,
            tipo_caja(tipo)
          )
        `)
        .eq('propuesta_id', propuestaId)
        .order('id', { ascending: true });

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar detalles de propuesta de embarque
  deletePropuestaDetalles: async ({ propuestaId }) => {
    try {
      // 1. Eliminar relaciones propuesta-pedidos
      const { error: relationsError } = await supabase
        .from('propuesta_pedidos')
        .delete()
        .eq('propuesta_id', propuestaId);

      if (relationsError) {
        console.error('Error al eliminar relaciones propuesta-pedido:', relationsError);
        // No bloqueamos la operación si falla (puede que no existan relaciones)
      }

      // 2. Eliminar detalles de propuesta
      const { error } = await supabase
        .from('preparativo_propuesta_embarque_detalles')
        .delete()
        .eq('propuesta_id', propuestaId);

      if (error) handleSupabaseError(error);

      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar archivos de OneDrive para una temporada/semana
  deletePreparativosFiles: async ({ anio, semana }) => {
    try {
      const response = await fetch(`${getNetlifyFunctionUrl('delete-preparativo')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anio,
          semana,
          action: 'deleteFiles'
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al eliminar archivos de OneDrive');
      }

      return result;
    } catch (error) {
      console.error('Error deleting preparativos files:', error);
      throw error;
    }
  },

  // ===== TARJAS API =====

  // Obtener tarjas de una propuesta con sus detalles
  getTarjas: async ({ propuestaId }) => {
    try {
      const { data, error } = await supabase
        .from('tarjas')
        .select(`
          *,
          detalles:tarjas_detalle (
            id,
            cantidad,
            propuesta_detalle_id,
            propuesta_detalle:preparativo_propuesta_embarque_detalles (
              id,
              producto_id,
              cantidad_cajas,
              clientes:cliente_id (
                id,
                nombre
              ),
              productos:producto_id (
                id,
                code,
                nombre,
                peso_neto_ruscus_kg,
                peso_total_caja_kg,
                bunches_box,
                stems_bunches
              )
            )
          )
        `)
        .eq('propuesta_id', propuestaId)
        .order('numero_consecutivo', { ascending: true });

      if (error) handleSupabaseError(error);

      return { data: data || [] };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Guardar distribución de tarjas (crear o actualizar)
  saveTarjas: async ({ propuestaId, tarjasData }) => {
    try {
      // tarjasData es un array de objetos: [{ numero_consecutivo, numero_folio, detalles: [{ propuesta_detalle_id, cantidad }] }]
      
      // Primero, eliminar todas las tarjas existentes de esta propuesta
      const { error: deleteError } = await supabase
        .from('tarjas')
        .delete()
        .eq('propuesta_id', propuestaId);

      if (deleteError) handleSupabaseError(deleteError);

      // Insertar nuevas tarjas
      for (const tarjaData of tarjasData) {
        const { detalles, ...tarjaInfo } = tarjaData;

        // Insertar tarja
        const { data: tarja, error: tarjaError } = await supabase
          .from('tarjas')
          .insert([{
            propuesta_id: propuestaId,
            numero_consecutivo: tarjaInfo.numero_consecutivo,
            numero_folio: tarjaInfo.numero_folio || null
          }])
          .select()
          .single();

        if (tarjaError) handleSupabaseError(tarjaError);

        // Insertar detalles de la tarja (solo los que tienen cantidad > 0)
        if (detalles && detalles.length > 0) {
          const detallesConTarjaId = detalles
            .filter(d => d.cantidad > 0)
            .map(detalle => ({
              tarja_id: tarja.id,
              propuesta_detalle_id: detalle.propuesta_detalle_id,
              cantidad: detalle.cantidad
            }));

          if (detallesConTarjaId.length > 0) {
            const { error: detallesError } = await supabase
              .from('tarjas_detalle')
              .insert(detallesConTarjaId);

            if (detallesError) handleSupabaseError(detallesError);
          }
        }
      }

      // Retornar las tarjas actualizadas
      return await preparativosAPI.getTarjas({ propuestaId });
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar todas las tarjas de una propuesta
  deleteTarjas: async ({ propuestaId }) => {
    try {
      const { error } = await supabase
        .from('tarjas')
        .delete()
        .eq('propuesta_id', propuestaId);

      if (error) handleSupabaseError(error);

      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener guía de despacho de un preparativo
  getGuiaDespacho: async ({ preparativoId }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_guiadespacho')
        .select('*')
        .eq('preparativo_id', preparativoId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear o actualizar guía de despacho
  upsertGuiaDespacho: async ({ preparativoId, guiaData }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_guiadespacho')
        .upsert({
          preparativo_id: preparativoId,
          ...guiaData,
          updated_at: new Date().toISOString()
        }, { onConflict: 'preparativo_id' })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Solicitar número de guía desde Defontana (Lambda)
  solicitarNumeroGuia: async ({ preparativoId }) => {
    try {
      const lambdaUrl = getLambdaUrl('defontana-guiadespacho');
      
      if (!lambdaUrl) {
        throw new Error('URL de Lambda para guía de despacho no configurada');
      }

      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preparativoId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al solicitar número de guía');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error en solicitarNumeroGuia:', error);
      throw error;
    }
  },

  // Ingresar guía de despacho desde Defontana a OneDrive (Lambda)
  ingresarGuiaDespacho: async ({ preparativoId, detalleText }) => {
    try {
      const lambdaUrl = getLambdaUrl('defontana-guia-ingresar');
      
      if (!lambdaUrl) {
        throw new Error('URL de Lambda para ingreso de guía no configurada');
      }

      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preparativoId, detalleText })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al ingresar guía de despacho');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error en ingresarGuiaDespacho:', error);
      throw error;
    }
  },

  // Ingresar factura local desde Defontana a OneDrive (Lambda)
  ingresarFacturaLocal: async ({ preparativoId, fecha }) => {
    try {
      const lambdaUrl = getLambdaUrl('defontana-factura-local-ingresar');

      if (!lambdaUrl) {
        throw new Error('URL de Lambda para ingreso de factura local no configurada');
      }

      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preparativoId, fecha })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al ingresar factura local');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error en ingresarFacturaLocal:', error);
      throw error;
    }
  },

  // Confirmar factura local ingresada (almacenar OneDrive URL de documento PDF)
  confirmarFacturaLocalIngresada: async ({ preparativoId, oneDrivePdfUrl, syncEventId, numero_factura, fecha }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_facturalocal')
        .upsert({
          preparativo_id: preparativoId,
          onedrive_pdf_url: oneDrivePdfUrl || null,
          sync_event_id: syncEventId,
          numero_factura: numero_factura || null,
          fecha: fecha || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'preparativo_id'
        })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener factura local por preparativo
  getFacturaLocalByPreparativo: async ({ preparativoId }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_facturalocal')
        .select('*')
        .eq('preparativo_id', preparativoId)
        .maybeSingle();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Confirmar guía ingresada (almacenar OneDrive URL de documento PDF)
  confirmarGuiaIngresada: async ({ preparativoId, oneDrivePdfUrl, oneDriveImageUrl, syncEventId }) => {
    try {
      const normalizedPdfUrl = oneDrivePdfUrl || oneDriveImageUrl || null;

      const { data, error } = await supabase
        .from('preparativo_guiadespacho')
        .upsert({
          preparativo_id: preparativoId,
          onedrive_pdf_url: normalizedPdfUrl,
          onedrive_image_url: oneDriveImageUrl || normalizedPdfUrl,
          sync_event_id_ingresar: syncEventId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'preparativo_id'
        })
        .select()
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener estado de sync_event
  getSyncEvent: async ({ syncEventId }) => {
    try {
      const { data, error } = await supabase
        .from('sync_events')
        .select('*')
        .eq('id', syncEventId)
        .single();

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Rebajar inventario para un preparativo
  rebajarInventarioPreparativo: async ({ preparativoId }) => {
    try {
      const url = getNetlifyFunctionUrl('preparativos-operations');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          operation: 'rebajar_inventario',
          preparativo_id: preparativoId
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al rebajar inventario');
      }

      return { data: result };
    } catch (error) {
      console.error('Error in rebajarInventarioPreparativo:', error);
      throw error;
    }
  },

  // Ajuste de cantidades sobre una propuesta Confirmada
  ajusteCantidades: async (operation, payload) => {
    const url = getNetlifyFunctionUrl('ajuste-cantidades-operations');

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ operation, ...payload })
    });

    const result = await response.json();

    if (!response.ok) {
      const error = new Error(result.error || 'Error en el ajuste de cantidades');
      error.details = result;
      throw error;
    }

    return result;
  },

  // Anulación de la propuesta por exportación fallida (Administrador)
  prepararAnulacionPropuesta: ({ preparativoId }) =>
    preparativosAPI.ajusteCantidades('preparar_anulacion', { preparativo_id: preparativoId }),

  anularPropuesta: ({ preparativoId, motivo, observaciones, destino }) =>
    preparativosAPI.ajusteCantidades('anular', { preparativo_id: preparativoId, motivo, observaciones, destino }),

  reiniciarPropuesta: ({ preparativoId }) =>
    preparativosAPI.ajusteCantidades('reiniciar', { preparativo_id: preparativoId }),

  // Última anulación registrada (se muestra con el paso en Rechazado)
  getAnulacionPropuesta: ({ preparativoId }) =>
    preparativosAPI.ajusteCantidades('anulacion', { preparativo_id: preparativoId }),

  // Ajustes ya registrados para un preparativo (histórico visible en el paso).
  // Solo los de la propuesta vigente: los anteriores a la última anulación
  // pertenecen a una propuesta que ya no existe.
  getAjustesCantidad: async ({ preparativoId }) => {
    try {
      const { data, error } = await supabase
        .from('preparativo_ajuste_cantidad')
        .select(`
          id,
          tipo,
          motivo,
          observaciones,
          registrado_por,
          fecha_registro,
          detalles:preparativo_ajuste_cantidad_detalle (
            id,
            propuesta_detalle_id,
            producto_id,
            cantidad_antes,
            cantidad_ajuste,
            cantidad_despues,
            productos:producto_id ( id, code, nombre )
          )
        `)
        .eq('preparativo_id', preparativoId)
        .order('fecha_registro', { ascending: false });

      if (error) handleSupabaseError(error);

      const registros = data || [];
      const ultimaAnulacionId = registros
        .filter((r) => r.tipo === 'ANULACION')
        .reduce((max, r) => Math.max(max, r.id), 0);

      return { data: registros.filter((r) => r.tipo !== 'ANULACION' && r.id > ultimaAnulacionId) };
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};

// Servicios para Pedidos Recurrentes
export const pedidosRecurrentesAPI = {
  // Obtener todos los pedidos recurrentes con filtros opcionales
  getAll: async (filters = {}) => {
    try {
      let query = supabase
        .from('pedidos_recurrentes')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente,
            email_contacto,
            pais,
            ciudad
          ),
          detalles:pedidos_recurrentes_detalle (
            id,
            producto_id,
            cantidad_cajas,
            precio_unitario,
            precio_total,
            productos:producto_id (
              id,
              nombre,
              code,
              category,
              bunches_box,
              stems_bunches,
              precio_unitario,
              precio_fob_mia,
              tipo_de_caja
            )
          )
        `);
      
      // Aplicar filtros
      if (filters.temporada) {
        query = query.eq('temporada', filters.temporada);
      }
      
      if (filters.cliente_id && filters.cliente_id !== 'all') {
        query = query.eq('cliente_id', filters.cliente_id);
      }
      
      if (filters.tipo && filters.tipo !== 'all') {
        query = query.eq('tipo', filters.tipo);
      }
      
      const { data, error } = await query.order('fecha_creacion', { ascending: false });
      
      if (error) handleSupabaseError(error);
      
      return { data: data || [] };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener pedido recurrente por ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('pedidos_recurrentes')
        .select(`
          *,
          clientes:cliente_id (
            id,
            nombre,
            codigo_cliente,
            email_contacto,
            pais,
            ciudad
          ),
          detalles:pedidos_recurrentes_detalle (
            id,
            producto_id,
            cantidad_cajas,
            precio_unitario,
            precio_total,
            productos:producto_id (
              id,
              nombre,
              code,
              category,
              bunches_box,
              stems_bunches,
              precio_unitario,
              precio_fob_mia,
              tipo_de_caja
            )
          )
        `)
        .eq('id', id)
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo pedido recurrente
  create: async (pedidoData) => {
    try {
      const { detalles, ...pedidoInfo } = pedidoData;
      
      // Insertar pedido recurrente
      const { data: pedido, error: pedidoError } = await supabase
        .from('pedidos_recurrentes')
        .insert([pedidoInfo])
        .select()
        .single();
      
      if (pedidoError) handleSupabaseError(pedidoError);
      
      // Insertar detalles si existen
      if (detalles && detalles.length > 0) {
        const detallesConPedidoId = detalles.map(detalle => ({
          pedido_recurrente_id: pedido.id,
          producto_id: detalle.producto_id,
          cantidad_cajas: detalle.cantidad_cajas,
          precio_unitario: detalle.precio_unitario,
          precio_total: detalle.precio_total
        }));
        
        const { error: detallesError } = await supabase
          .from('pedidos_recurrentes_detalle')
          .insert(detallesConPedidoId);
        
        if (detallesError) handleSupabaseError(detallesError);
      }
      
      return { data: pedido };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar pedido recurrente
  update: async (id, pedidoData) => {
    try {
      const { detalles, ...pedidoInfo } = pedidoData;
      
      // Actualizar pedido recurrente
      const { error: pedidoError } = await supabase
        .from('pedidos_recurrentes')
        .update({
          ...pedidoInfo,
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (pedidoError) handleSupabaseError(pedidoError);
      
      // Actualizar detalles si existen
      if (detalles && detalles.length > 0) {
        // Primero eliminar detalles existentes
        const { error: deleteError } = await supabase
          .from('pedidos_recurrentes_detalle')
          .delete()
          .eq('pedido_recurrente_id', id);
        
        if (deleteError) handleSupabaseError(deleteError);
        
        // Insertar nuevos detalles
        const detallesConPedidoId = detalles.map(detalle => ({
          pedido_recurrente_id: id,
          producto_id: detalle.producto_id,
          cantidad_cajas: detalle.cantidad_cajas,
          precio_unitario: detalle.precio_unitario,
          precio_total: detalle.precio_total
        }));
        
        const { error: insertError } = await supabase
          .from('pedidos_recurrentes_detalle')
          .insert(detallesConPedidoId);
        
        if (insertError) handleSupabaseError(insertError);
      }
      
      // Obtener el pedido completo con detalles
      return await pedidosRecurrentesAPI.getById(id);
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar pedido recurrente
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('pedidos_recurrentes')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },
};

// Servicios para Tipo de Caja
export const tipoCajaAPI = {
  // Obtener todos los tipos de caja
  getAll: async (filters = {}) => {
    try {
      let query = supabase.from('tipo_caja').select('*');
      
      // Filtrar solo activos si se especifica
      if (filters.activo !== undefined) {
        query = query.eq('activo', filters.activo);
      }
      
      // Aplicar búsqueda si existe
      if (filters.search) {
        query = query.or(`tipo.ilike.%${filters.search}%,descripcion.ilike.%${filters.search}%`);
      }
      
      const { data, error } = await query.order('tipo', { ascending: true });
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener tipo de caja por ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('tipo_caja')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo tipo de caja
  create: async (tipoCajaData) => {
    try {
      const { data, error } = await supabase
        .from('tipo_caja')
        .insert([{
          ...tipoCajaData,
          fecha_creacion: new Date().toISOString(),
          fecha_modificacion: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar tipo de caja
  update: async (id, tipoCajaData) => {
    try {
      const { data, error } = await supabase
        .from('tipo_caja')
        .update({
          ...tipoCajaData,
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar tipo de caja (soft delete - marca como inactivo)
  delete: async (id) => {
    try {
      // Verificar si hay productos usando este tipo de caja
      const { data: productos, error: checkError } = await supabase
        .from('productos')
        .select('id')
        .eq('tipo_caja_id', id)
        .limit(1);
      
      if (checkError) handleSupabaseError(checkError);
      
      if (productos && productos.length > 0) {
        throw new Error('No se puede eliminar este tipo de caja porque está siendo usado por productos');
      }
      
      // Marcar como inactivo en lugar de eliminar
      const { error } = await supabase
        .from('tipo_caja')
        .update({ 
          activo: false,
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar permanentemente (solo si no hay productos asociados)
  hardDelete: async (id) => {
    try {
      // Verificar si hay productos usando este tipo de caja
      const { data: productos, error: checkError } = await supabase
        .from('productos')
        .select('id')
        .eq('tipo_caja_id', id)
        .limit(1);
      
      if (checkError) handleSupabaseError(checkError);
      
      if (productos && productos.length > 0) {
        throw new Error('No se puede eliminar este tipo de caja porque está siendo usado por productos');
      }
      
      const { error } = await supabase
        .from('tipo_caja')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },
};

// Función helper para manejar errores de API (mantenida para compatibilidad)
export const handleAPIError = (error) => {
  console.error('API Error:', error);
  
  // Personalizar mensajes de error
  if (error.message.includes('Failed to fetch')) {
    return 'Error de conexión. Verifica tu conexión a internet.';
  }
  
  if (error.message.includes('404')) {
    return 'Recurso no encontrado.';
  }
  
  if (error.message.includes('500')) {
    return 'Error interno del servidor. Intenta nuevamente más tarde.';
  }
  
  if (error.message.includes('JWT')) {
    return 'Sesión expirada. Por favor, inicia sesión nuevamente.';
  }
  
  if (error.message.includes('duplicate key')) {
    return 'Ya existe un registro con esos datos.';
  }
  
  return error.message || 'Ha ocurrido un error inesperado.';
};

// Servicios para Transportes
export const transportesAPI = {
  // Obtener todos los transportes con filtros opcionales
  getAll: async (filters = {}) => {
    try {
      let query = supabase.from('transportes').select('*');
      
      // Aplicar filtros si existen
      if (filters.search) {
        query = query.ilike('nombre', `%${filters.search}%`);
      }
      
      if (filters.tipo && filters.tipo !== 'all') {
        query = query.eq('tipo', filters.tipo);
      }
      
      // Solo transportes activos por defecto
      if (filters.activo !== false) {
        query = query.eq('activo', true);
      }
      
      const { data, error } = await query.order('nombre', { ascending: true });
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Obtener un transporte por ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('transportes')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo transporte
  create: async (transporteData) => {
    try {
      const { data, error } = await supabase
        .from('transportes')
        .insert([{
          ...transporteData,
          fecha_creacion: new Date().toISOString(),
          fecha_modificacion: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Actualizar transporte
  update: async (id, transporteData) => {
    try {
      const { data, error } = await supabase
        .from('transportes')
        .update({
          ...transporteData,
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar transporte (soft delete - marcar como inactivo)
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('transportes')
        .update({ 
          activo: false,
          fecha_modificacion: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Eliminar transporte permanentemente (hard delete)
  hardDelete: async (id) => {
    try {
      const { error } = await supabase
        .from('transportes')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};

// Servicios para Créditos de Cliente
export const creditosAPI = {
  // Obtener créditos por pedido_id
  getByPedidoId: async (pedidoId) => {
    try {
      const { data, error } = await supabase
        .from('creditos_cliente')
        .select(`
          *,
          productos:producto_id (
            id,
            nombre
          )
        `)
        .eq('pedido_id', pedidoId)
        .order('fecha_creacion', { ascending: false });
      
      if (error) handleSupabaseError(error);
      
      return { data: data || [] };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Crear nuevo crédito con validación de cantidad
  create: async (creditData) => {
    try {
      // Validar que cantidad_credito es un número válido
      if (typeof creditData.cantidad_credito !== 'number' || isNaN(creditData.cantidad_credito)) {
        throw new Error('Cantidad debe ser un número válido');
      }
      
      // Validar que cantidad_credito es positiva
      if (creditData.cantidad_credito <= 0) {
        throw new Error('La cantidad debe ser mayor a 0');
      }
      
      // Validar precisión decimal (máximo 2 decimales)
      const decimalRegex = /^\d+(\.\d{1,2})?$/;
      if (!decimalRegex.test(creditData.cantidad_credito.toString())) {
        throw new Error('Cantidad debe tener máximo 2 decimales');
      }
      
      // Primero validar que la cantidad no exceda la cantidad pedida
      const { data: pedidoDetalle, error: detalleError } = await supabase
        .from('pedidos_detalle')
        .select('cantidad_cajas')
        .eq('pedido_id', creditData.pedido_id)
        .eq('producto_id', creditData.producto_id)
        .single();
      
      if (detalleError) {
        throw new Error('No se encontró el producto en el pedido');
      }
      
      // Obtener créditos existentes para este producto en este pedido
      const { data: creditosExistentes, error: creditosError } = await supabase
        .from('creditos_cliente')
        .select('cantidad_credito')
        .eq('pedido_id', creditData.pedido_id)
        .eq('producto_id', creditData.producto_id);
      
      if (creditosError) handleSupabaseError(creditosError);
      
      // Calcular total de créditos existentes
      const totalCreditosExistentes = creditosExistentes?.reduce(
        (sum, c) => sum + c.cantidad_credito, 
        0
      ) || 0;
      
      // Validar que el total de créditos no exceda la cantidad pedida
      const totalCreditos = totalCreditosExistentes + creditData.cantidad_credito;
      const cantidadPedida = pedidoDetalle.cantidad_cajas;
      if (totalCreditos > cantidadPedida) {
        throw new Error(
          `La cantidad total de créditos (${totalCreditos}) no puede exceder la cantidad pedida (${cantidadPedida})`
        );
      }
      
      // Crear el crédito
      const { data, error } = await supabase
        .from('creditos_cliente')
        .insert([creditData])
        .select(`
          *,
          productos:producto_id (
            id,
            nombre
          )
        `)
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      if (error.message) {
        throw error;
      }
      handleSupabaseError(error);
    }
  },

  // Actualizar crédito existente con validación
  update: async (id, creditData) => {
    try {
      // Obtener el crédito actual
      const { data: creditoActual, error: currentError } = await supabase
        .from('creditos_cliente')
        .select('pedido_id, producto_id, cantidad_credito')
        .eq('id', id)
        .single();
      
      if (currentError) handleSupabaseError(currentError);
      
      // Validar cantidad si se está actualizando
      if (creditData.cantidad_credito !== undefined) {
        // Validar que cantidad_credito es un número válido
        if (typeof creditData.cantidad_credito !== 'number' || isNaN(creditData.cantidad_credito)) {
          throw new Error('Cantidad debe ser un número válido');
        }
        
        // Validar que cantidad_credito es positiva
        if (creditData.cantidad_credito <= 0) {
          throw new Error('La cantidad debe ser mayor a 0');
        }
        
        // Validar precisión decimal (máximo 2 decimales)
        const decimalRegex = /^\d+(\.\d{1,2})?$/;
        if (!decimalRegex.test(creditData.cantidad_credito.toString())) {
          throw new Error('Cantidad debe tener máximo 2 decimales');
        }
        
        const { data: pedidoDetalle, error: detalleError } = await supabase
          .from('pedidos_detalle')
          .select('cantidad_cajas')
          .eq('pedido_id', creditoActual.pedido_id)
          .eq('producto_id', creditoActual.producto_id)
          .single();
        
        if (detalleError) {
          throw new Error('No se encontró el producto en el pedido');
        }
        
        // Obtener otros créditos existentes (excluyendo el actual)
        const { data: otrosCreditos, error: creditosError } = await supabase
          .from('creditos_cliente')
          .select('cantidad_credito')
          .eq('pedido_id', creditoActual.pedido_id)
          .eq('producto_id', creditoActual.producto_id)
          .neq('id', id);
        
        if (creditosError) handleSupabaseError(creditosError);
        
        const totalOtrosCreditos = otrosCreditos?.reduce(
          (sum, c) => sum + c.cantidad_credito, 
          0
        ) || 0;
        
        const totalCreditos = totalOtrosCreditos + creditData.cantidad_credito;
        const cantidadPedida = pedidoDetalle.cantidad_cajas;
        if (totalCreditos > cantidadPedida) {
          throw new Error(
            `La cantidad total de créditos (${totalCreditos}) no puede exceder la cantidad pedida (${cantidadPedida})`
          );
        }
      }
      
      // Actualizar el crédito
      const { data, error } = await supabase
        .from('creditos_cliente')
        .update(creditData)
        .eq('id', id)
        .select(`
          *,
          productos:producto_id (
            id,
            nombre
          )
        `)
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      if (error.message) {
        throw error;
      }
      handleSupabaseError(error);
    }
  },

  // Eliminar crédito
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('creditos_cliente')
        .delete()
        .eq('id', id);
      
      if (error) handleSupabaseError(error);
      
      return { success: true };
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};

// Servicios para Email Responses
export const emailResponsesAPI = {
  // Obtener estadísticas de emails procesados hoy
  getStats: async () => {
    try {
      const url = getNetlifyFunctionUrl('email-response-stats');
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error fetching email stats:', error);
      throw error;
    }
  },

  // Obtener lista detallada de emails procesados hoy
  getTodayResponses: async () => {
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const tomorrowStr = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('email_responses_log')
        .select('*')
        .gte('processed_at', todayStr)
        .lt('processed_at', tomorrowStr)
        .order('processed_at', { ascending: false });

      if (error) handleSupabaseError(error);

      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};

// Servicios para Message Templates
export const messageTemplatesAPI = {
  // Obtener plantilla de mensaje por tipo de paso
  getMessageTemplate: async (stepType) => {
    try {
      const { data, error } = await supabase
        .from('preparativos_message_templates')
        .select('*')
        .eq('step_type', stepType)
        .maybeSingle();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Guardar o actualizar plantilla de mensaje (UPSERT)
  saveMessageTemplate: async (stepType, messageTemplate) => {
    try {
      const { data, error } = await supabase
        .from('preparativos_message_templates')
        .upsert(
          {
            step_type: stepType,
            message_template: messageTemplate,
            updated_at: new Date().toISOString()
          },
          {
            onConflict: 'step_type',
            returning: 'representation'
          }
        )
        .select()
        .single();
      
      if (error) handleSupabaseError(error);
      
      return { data };
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};

export const pesoGrossAPI = {
  get: async ({ temporada, semana }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('peso-gross-operations');
      const response = await fetch(`${functionUrl}?temporada=${temporada}&semana=${semana}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al obtener peso gross');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error in pesoGrossAPI.get:', error);
      throw error;
    }
  },

  create: async ({ temporada, semana, peso_actual = null }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('peso-gross-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ temporada, semana, peso_actual })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear registro de peso gross');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error in pesoGrossAPI.create:', error);
      throw error;
    }
  },

  update: async (id, { peso_actual }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('peso-gross-operations');
      const response = await fetch(`${functionUrl}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ peso_actual })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar peso gross');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error in pesoGrossAPI.update:', error);
      throw error;
    }
  },

  uploadGuia: async (id, file) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const formData = new FormData();
      formData.append('file', file);

      const functionUrl = getNetlifyFunctionUrl('peso-gross-operations');
      const response = await fetch(`${functionUrl}/${id}/upload-guia`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al subir guía aérea');
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Error in pesoGrossAPI.uploadGuia:', error);
      throw error;
    }
  }
};

// Servicios para Tarifas del Embarque
export const tarifasAnualesAPI = {
  // Obtener todas las tarifas
  getAll: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('tarifas-anuales-operations');
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al obtener tarifas');
      }

      const result = await response.json();
      return { data: result.data };
    } catch (error) {
      console.error('Error in tarifasAnualesAPI.getAll:', error);
      throw error;
    }
  },

  // Crear nueva tarifa
  create: async (tarifaData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('tarifas-anuales-operations');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tarifaData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear tarifa');
      }

      const result = await response.json();
      return { data: result.data };
    } catch (error) {
      console.error('Error in tarifasAnualesAPI.create:', error);
      throw error;
    }
  },

  // Actualizar tarifa
  update: async (id, tarifaData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('tarifas-anuales-operations');
      const response = await fetch(`${functionUrl}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tarifaData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar tarifa');
      }

      const result = await response.json();
      return { data: result.data };
    } catch (error) {
      console.error('Error in tarifasAnualesAPI.update:', error);
      throw error;
    }
  },

  // Eliminar tarifa
  delete: async (id) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const functionUrl = getNetlifyFunctionUrl('tarifas-anuales-operations');
      const response = await fetch(`${functionUrl}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar tarifa');
      }

      return { data: { id } };
    } catch (error) {
      console.error('Error in tarifasAnualesAPI.delete:', error);
      throw error;
    }
  }
};

// =====================================================
// Despachos Nacionales - Factura Local
// =====================================================

export const despachoNacionalesAPI = {
  // Preview de Factura Local para pedido nacional
  previewFacturaLocalNacional: async ({ pedidoId, fecha }) => {
    try {
      const lambdaUrl = getLambdaUrl('defontana-factura-local-ingresar');
      if (!lambdaUrl) {
        throw new Error('Lambda URL no configurada para defontana-factura-local-ingresar');
      }

      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pedidoId,
          fecha,
          preview: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error obteniendo vista previa de factura');
      }

      const data = await response.json();
      
      if (!data.invoiceData) {
        throw new Error(data.error || 'No se recibieron datos de factura');
      }

      return data.invoiceData;
    } catch (error) {
      console.error('Error en previewFacturaLocalNacional:', error);
      throw error;
    }
  },

  // Generar Factura Local para pedido nacional (async)
  generarFacturaLocalNacional: async ({ pedidoId, fecha }) => {
    try {
      const lambdaUrl = getLambdaUrl('defontana-factura-local-ingresar');
      if (!lambdaUrl) {
        throw new Error('Lambda URL no configurada para defontana-factura-local-ingresar');
      }

      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pedidoId,
          fecha,
          preview: false
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error iniciando generación de factura');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error iniciando generación');
      }

      return {
        success: true,
        syncEventId: data.syncEventId,
        message: data.message
      };
    } catch (error) {
      console.error('Error en generarFacturaLocalNacional:', error);
      throw error;
    }
  }
};
